import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { NavigationBar } from "components/common/NavigationBar";
import type { TraversalControlsProps } from "./types";

export const TraversalControls = ({
  editingLabel,
  headerAction,
  selectedIndex,
  total,
  seenCount,
  onPrev,
  onNext,
  onSelectIndex,
  onNextUnseen,
  isAllMode = false,
}: TraversalControlsProps) => {
  const { t } = useTranslation();

  const unseenCount = total - seenCount;
  const seenPercentage = total > 0 ? Math.round((seenCount / total) * 100) : 0;

  return (
    <div className="shrink-0 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-gray-700">
          {editingLabel}
        </span>
        {headerAction}
      </div>
      {!isAllMode && (
        <>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-sm font-bold text-green-600">
              {t("smartSurveyor.traversal.seenCount", {
                count: seenCount,
              })}
            </span>
            <span className="text-sm text-gray-400">&bull;</span>
            <span className="text-sm font-bold text-red-500">
              {t("smartSurveyor.traversal.unseenCount", {
                count: unseenCount,
              })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${seenPercentage}%` }}
              />
            </div>
            <span className="shrink-0 text-xs font-semibold text-gray-600">
              {seenPercentage}%
            </span>
          </div>
        </>
      )}
      <div className="flex items-center justify-between gap-3">
        <NavigationBar
          selectedIndex={selectedIndex}
          total={total}
          canPrev={selectedIndex > 0}
          canNext={selectedIndex < total - 1}
          onPrev={onPrev}
          onNext={onNext}
          onSelectIndex={onSelectIndex}
          prevLabel={t("common.prev")}
          nextLabel={t("common.next")}
        />
        {!isAllMode && unseenCount > 0 && (
          <button
            type="button"
            onClick={onNextUnseen}
            className="shrink-0 flex items-center gap-0.5 text-xs font-medium text-green-600 hover:text-green-700 transition-colors"
          >
            <span>{t("smartSurveyor.traversal.nextUnseen")}</span>
            <ChevronRight size={14} strokeWidth={2.2} />
          </button>
        )}
      </div>
    </div>
  );
};
